<template>
  <AuthLayout>
    <template #title> Inscription </template>
    <template #subTitle> Renseignez vos infos pour continuer </template>
    <template #redirectQuestion> Déjà un compte? </template>
    <template #redirectLink>
      <router-link to="/login">Connectez-vous!</router-link>
    </template>
    <template #superAction>
      <router-link to="/">Continuer sans compte</router-link>
    </template>
    <form
      class="space-y-6"
      v-bus:EVENTS.USER.REGISTER.submit.prevent
      v-redirect-on="{
        event: $constants.EVENTS.USER.REGISTER_SUCCESS,
        to: '/'
      }"
    >
      <!-- Nom et Prénom -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 font-medium text-gray-700">Nom</label>
          <input
            name="lastName"
            type="text"
            required
            class="inputComponent"
            placeholder="Votre nom"
          />
        </div>
        <div>
          <label class="block mb-1 font-medium text-gray-700">Prénom</label>
          <input
            name="firstName"
            type="text"
            required
            class="inputComponent"
            placeholder="Votre prénom"
          />
        </div>
      </div>

      <!-- Téléphone -->
      <div>
        <label class="block mb-1 font-medium text-gray-700">Téléphone</label>
        <div class="flex gap-2">
          <Listbox
            v-model="selectedCountry"
            as="div"
            class="relative flex-1 max-w-[120px]"
          >
            <ListboxButton
              class="inputComponent w-full flex items-center gap-2 pr-2"
            >
              <ImgComponent
                v-if="selectedCountry"
                :src="selectedCountry.flag"
                class="w-5 h-4 object-cover rounded-sm"
              />
              <span class="flex-1 text-left">{{
                selectedCountry?.code || 'Choisir'
              }}</span>
              <span class="text-gray-400">▼</span>
            </ListboxButton>
            <ListboxOptions
              class="absolute z-50 mt-1 max-h-60 w-auto min-w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-sm"
            >
              <ListboxOption
                v-for="country in countries"
                :key="country.code"
                :value="country"
                class="relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-gray-100 flex items-center gap-2 whitespace-nowrap"
              >
                <ImgComponent
                  :src="country.flag"
                  class="w-5 h-4 object-cover rounded-sm"
                />
                <span class="font-medium">+{{ country.code }}</span>
                <span class="text-gray-500 ml-1">{{ country.name }}</span>
              </ListboxOption>
            </ListboxOptions>
          </Listbox>
          <input
            name="phoneNumber"
            type="tel"
            required
            class="flex-1! inputComponent"
            placeholder="01XXXXXXXX"
            pattern="^01[0-9]{8}$"
            maxlength="10"
          />
          <input
            name="phonePrefix"
            type="hidden"
            :value="selectedCountry?.code || '229'"
          />
        </div>
      </div>

      <!-- Email -->
      <div>
        <label class="block mb-1 font-medium text-gray-700">Email</label>
        <input
          name="email"
          type="email"
          class="inputComponent"
          placeholder="email@exemple.com"
        />
      </div>

      <!-- Mot de passe -->
      <div>
        <label class="block mb-1 font-medium text-gray-700">Mot de passe</label>
        <input
          name="password"
          type="password"
          required
          class="inputComponent"
          placeholder="********"
        />
      </div>

      <!-- WhatsApp -->
      <div>
        <label class="block mb-1 font-medium text-gray-700"
          >Numéro WhatsApp (optionnel)</label
        >
        <input
          name="whatsappNumber"
          type="tel"
          class="inputComponent"
          placeholder="Ce champ n'est pas obligatoire"
        />
      </div>

      <!-- Referral Code -->
      <input name="referralCode" type="hidden" :value="referralCode" />

      <!-- Case à cocher CGU -->
      <div class="flex items-start gap-3">
        <input
          id="acceptCgu"
          name="acceptCgu"
          type="checkbox"
          required
          class="mt-1 w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
        />
        <label for="acceptCgu" class="text-sm text-gray-600 leading-snug">
          J'accepte les
          <button
            type="button"
            class="text-primary-600 underline hover:text-primary-700 font-medium"
            @click="showCguDialog"
          >
            conditions générales d'utilisation
          </button>
        </label>
      </div>

      <!-- Bouton continuer -->
      <button
        type="submit"
        class="buttonComponent w-full"
        :disabled="$stores?.user?.state?.loading"
        :loading="$stores?.user?.state?.loading"
      >
        S'inscrire
      </button>

      <!-- Dialog CGU (bottomsheet avec iframe) -->
      <CguDialog ref="cguDialog" />
    </form>
  </AuthLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/vue';
import CguDialog from './CguDialog.vue';

const countries = [
  { code: '229', name: 'Bénin', flag: 'https://flagcdn.com/w40/bj.png' },
  // { code: '228', name: 'Togo', flag: 'https://flagcdn.com/w40/tg.png' },
  // {
  //   code: '225',
  //   name: "Côte d'Ivoire",
  //   flag: 'https://flagcdn.com/w40/ci.png'
  // },
  // { code: '226', name: 'Burkina Faso', flag: 'https://flagcdn.com/w40/bf.png' },
  // { code: '223', name: 'Mali', flag: 'https://flagcdn.com/w40/ml.png' },
  // { code: '241', name: 'Gabon', flag: 'https://flagcdn.com/w40/ga.png' },
  // { code: '227', name: 'Niger', flag: 'https://flagcdn.com/w40/ne.png' },
  // { code: '221', name: 'Sénégal', flag: 'https://flagcdn.com/w40/sn.png' }
];

const selectedCountry = ref(countries[0]);

const route = useRoute();
const referralCode = computed(() => {
  return route.query.ref || '';
});

const cguDialog = ref(null);

function showCguDialog() {
  cguDialog.value?.show();
}
</script>